/**
 * The starter prop library: what each item is, its real size, and how sure that size is. Sizes come
 * from the planning site's Dimensions catalogue (context/dimensions.json) where it has them; the rest
 * are assumed until someone measures them, and the app says so ("size assumed").
 *
 * Sizes are metres: width (side to side), depth (front to back), height. Placed unturned, an item's
 * front faces the stage (+y in the hall frame).
 */

import { FT, IN } from './units';

export type Category = 'seating' | 'tables' | 'stage' | 'banners' | 'signs' | 'carpet' | 'people' | 'outside';

export const CATEGORIES: readonly { id: Category; name: string }[] = [
  { id: 'seating', name: 'Seating' },
  { id: 'tables', name: 'Tables' },
  { id: 'stage', name: 'Stage' },
  { id: 'banners', name: 'Banners and flags' },
  { id: 'signs', name: 'Signs' },
  { id: 'carpet', name: 'Carpet and floor' },
  { id: 'people', name: 'People' },
  { id: 'outside', name: 'Outside' },
];

/** measured: someone measured it; catalogue: from the planning catalogue, still to confirm; assumed: nobody has. */
export type PropSizeState = 'measured' | 'catalogue' | 'assumed';

/** How the app draws the item. */
export type Make = 'banquet-chair' | 'folding-table' | 'platform' | 'backdrop-frame' | 'panel' | 'minbar' | 'carpet' | 'tent' | 'person';

/** floor: stands on whatever is under it; wall: hangs flat on a wall; stand: a panel held up off the floor. */
export type Mount = 'floor' | 'wall' | 'stand';

/**
 * What holds a stand-mounted panel up: two poles, one flag pole, or nothing drawn because it hangs on its
 * host (see `hostedBy`). One with no host to hang on stands on poles, so it never floats.
 */
export type Support = 'poles' | 'pole' | 'none';

export type Counted = 'chairs' | 'tables' | 'banners' | 'people';

export type Pose = 'standing' | 'floor' | 'chair' | 'minbar' | 'platform';
export type BodyBuild = 'slim' | 'medium' | 'broad';
export type Dress = 'men' | 'women';

export interface PersonDefaults {
  dress: Dress;
  pose: Pose;
  /** Standing height, metres. */
  height: number;
  build: BodyBuild;
}

export interface CatalogueItem {
  id: string;
  name: string;
  category: Category;
  size: readonly [number, number, number];
  state: PropSizeState;
  /** When only some of the three sizes are assumed. */
  assumed?: readonly ('width' | 'depth' | 'height')[];
  make: Make;
  mount: Mount;
  counts?: Counted;
  /** Which of its sizes a lead may change (a carpet strip's length, a banner's width). */
  resizable?: readonly ('width' | 'depth' | 'height')[];
  /** Panels: the bottom edge's height above the floor on a wall; the top edge's height on a stand. Metres. */
  hangAt?: number;
  support?: Support;
  /** What it hangs on when placed near one (a print on its frame), by catalogue id. */
  hostedBy?: string;
  /** Hosts: how far in front of their middle line the things hung on them sit. Metres. */
  face?: number;
  person?: PersonDefaults;
  source?: string;
  note?: string;
}

const STAND = 'stand' as const;

export const CATALOGUE: readonly CatalogueItem[] = [
  {
    id: 'chair-banquet-taha', name: 'Banquet chair, Taha', category: 'seating', size: [18 * IN, 21 * IN, 34 * IN],
    state: 'assumed', make: 'banquet-chair', mount: 'floor', counts: 'chairs',
    note: 'Padded and stackable, seat 18 in high. Size and colour assumed until a photo or the model name settles them.',
  },
  {
    id: 'table-folding-6', name: 'Folding table, 6 ft', category: 'tables', size: [72 * IN, 30 * IN, 29 * IN],
    state: 'assumed', assumed: ['depth', 'height'], make: 'folding-table', mount: 'floor', counts: 'tables',
    source: 'Keshweneye packet, Layali al-Qadr 2026 (the Mawkeb\'s own, 6 ft)', note: '30 in wide and 29 in high assumed.',
  },
  {
    id: 'table-folding-8', name: 'Folding table, 8 ft', category: 'tables', size: [96 * IN, 30 * IN, 29 * IN],
    state: 'assumed', assumed: ['depth', 'height'], make: 'folding-table', mount: 'floor', counts: 'tables',
    note: '30 in wide and 29 in high assumed.',
  },
  {
    id: 'backdrop-frame', name: 'Backdrop frame', category: 'stage', size: [25 * FT, 2 * FT, 13 * FT],
    state: 'measured', assumed: ['depth'], make: 'backdrop-frame', mount: 'floor', face: 0.03,
    source: 'Setup packet, Sayyida Fatima 2026', note: 'By Kassem El Saghir, adjustable. Its feet (2 ft deep) are assumed.',
  },
  {
    id: 'backdrop-print', name: 'Backdrop print', category: 'stage', size: [25 * FT, 0.5 * IN, 10.78 * FT],
    state: 'catalogue', make: 'panel', mount: STAND, support: 'none', hostedBy: 'backdrop-frame', resizable: ['width', 'height'], hangAt: 13 * FT,
    source: 'Media plan, Layali al-Qadr 2026', note: 'Grommeted; check it against the 25 x 13 ft frame before printing again.',
  },
  {
    id: 'minbar', name: 'Minbar', category: 'stage', size: [3 * FT, 5 * FT, 6.5 * FT],
    state: 'assumed', make: 'minbar', mount: 'floor',
    note: 'Shape and size assumed until it is photographed and measured, or scanned.',
  },
  {
    id: 'podium-sign', name: 'Podium sign', category: 'stage', size: [32 * IN, 0.5 * IN, 28 * IN],
    state: 'catalogue', make: 'panel', mount: STAND, support: 'poles', resizable: ['width', 'height'], hangAt: 58 * IN,
    source: 'Setup plan, Layali al-Qadr 2026',
    note: 'The sign on the minbar. Shown on a stand until the minbar is photographed and it is clear where the sign hangs. Confirm the size for the minbar in use before designing the artwork.',
  },
  {
    id: 'platform-speaker-taha', name: 'Speaker platform, Taha', category: 'stage', size: [24 * IN, 24 * IN, 24 * IN],
    state: 'measured', make: 'platform', mount: 'floor',
    source: 'Setup packet, Layali al-Qadr 2026', note: 'Taha\'s, kept in Zahraa Hall; lifts the reciter 24 in. Its covering is assumed.',
  },
  {
    id: 'banner-sisters', name: 'Sisters\' banner', category: 'banners', size: [25 * FT, 0.5 * IN, 12 * FT],
    state: 'assumed', make: 'panel', mount: 'wall', counts: 'banners', resizable: ['width', 'height'], hangAt: 2 * FT,
    source: 'Media plan, Layali al-Qadr 2026', note: 'Written as 25 x 12 with no unit; feet assumed.',
  },
  {
    id: 'banner-shuttle', name: 'Shuttle station banner', category: 'banners', size: [10 * FT, 0.5 * IN, 2 * FT],
    state: 'measured', make: 'panel', mount: 'wall', counts: 'banners', resizable: ['width', 'height'], hangAt: 6 * FT,
    source: 'Media plan, Layali al-Qadr 2026',
  },
  {
    id: 'flag-pole', name: 'Flag on a pole', category: 'banners', size: [3 * FT, 0.25 * IN, 5 * FT],
    state: 'assumed', make: 'panel', mount: STAND, support: 'pole', counts: 'banners', resizable: ['width', 'height'], hangAt: 8 * FT,
    note: 'A 3 x 5 ft flag on an 8 ft pole, assumed.',
  },
  {
    id: 'sign-door', name: 'Door sign', category: 'signs', size: [17 * IN, 0.25 * IN, 11 * IN],
    state: 'measured', make: 'panel', mount: 'wall', resizable: ['width', 'height'], hangAt: 54 * IN,
    source: 'Media packet, Sayyida Fatima 2026', note: 'Entrance, bathroom and do-not-enter signs share this template.',
  },
  {
    id: 'sign-donation', name: 'Donation box sign', category: 'signs', size: [8.5 * IN, 0.25 * IN, 11 * IN],
    state: 'measured', make: 'panel', mount: 'wall', resizable: ['width', 'height'], hangAt: 36 * IN,
    source: 'Media packet, Sayyida Fatima 2026',
  },
  {
    id: 'carpet-strip-zahraa', name: 'Carpet strip', category: 'carpet', size: [12 * FT, 74 * FT, 0.4 * IN],
    state: 'measured', assumed: ['height'], make: 'carpet', mount: 'floor', resizable: ['depth'],
    source: 'Setup packet, Sayyida Fatima 2026', note: 'The Mawkeb\'s own; four of them. For a split hall they stop at the 37 ft line. Do not cut.',
  },
  {
    id: 'person-man-standing', name: 'Man, standing', category: 'people', size: [20 * IN, 12 * IN, 5 * FT + 10 * IN],
    state: 'assumed', make: 'person', mount: 'floor', counts: 'people',
    person: { dress: 'men', pose: 'standing', height: 5 * FT + 10 * IN, build: 'medium' },
  },
  {
    id: 'person-woman-standing', name: 'Woman, standing', category: 'people', size: [20 * IN, 13 * IN, 5 * FT + 4 * IN],
    state: 'assumed', make: 'person', mount: 'floor', counts: 'people',
    person: { dress: 'women', pose: 'standing', height: 5 * FT + 4 * IN, build: 'medium' },
  },
  {
    id: 'person-man-floor', name: 'Man, seated on the carpet', category: 'people', size: [30 * IN, 26 * IN, 36 * IN],
    state: 'assumed', make: 'person', mount: 'floor', counts: 'people',
    person: { dress: 'men', pose: 'floor', height: 5 * FT + 10 * IN, build: 'medium' },
  },
  {
    id: 'person-woman-floor', name: 'Woman, seated on the carpet', category: 'people', size: [30 * IN, 26 * IN, 33 * IN],
    state: 'assumed', make: 'person', mount: 'floor', counts: 'people',
    person: { dress: 'women', pose: 'floor', height: 5 * FT + 4 * IN, build: 'medium' },
  },
  {
    id: 'tent-white-taha', name: 'White tent, Taha', category: 'outside', size: [10 * FT, 10 * FT, 10 * FT],
    state: 'measured', assumed: ['height'], make: 'tent', mount: 'floor', source: 'Setup packet, Sayyida Fatima 2026',
  },
  {
    id: 'tent-black', name: 'Black tent', category: 'outside', size: [20 * FT, 10 * FT, 10 * FT],
    state: 'measured', assumed: ['height'], make: 'tent', mount: 'floor', source: 'Setup packet, Sayyida Fatima 2026',
    note: 'Bought for the women\'s Madeef.',
  },
];

const byId = new Map(CATALOGUE.map((c) => [c.id, c]));

export function catalogueItem(id: string): CatalogueItem | undefined {
  return byId.get(id);
}

/** True when any of the item's sizes is a guess, so the library tags it "size assumed". */
export function sizeAssumed(item: CatalogueItem): boolean {
  return item.state === 'assumed' || (item.assumed?.length ?? 0) > 0;
}
